import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { PinterestFill } from '@archipro-design/icons';
import { useAtomValue, useSetAtom } from 'jotai';
import {
    ArrowUpRight,
    MoreHorizontal,
    Bookmark,
    Share,
    MessageSquareText,
} from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { button } from '~/ui-rocco/button-system';
import {
    Drawer,
    DrawerContent,
    DrawerTitle,
    DrawerDescription,
} from '@rocco/components/drawer';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { uiStateAtomSocialSharingModalIsOpened } from '@rocco/ui/social-sharing/components/SocialSharingModal';
import { useFollowSelectorProxy } from '~/modules/moodboards/hooks/use-follow-selector-proxy';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import {
    isNativeShareAvailable,
    shareNative,
    type ShareOptions,
} from '~/modules/social-sharing/utils/sharing';
import { shareTargetAtom } from '~/modules/social-sharing/state/share-target';
import { TileShareMenu } from './TileShareMenu';
import { buildPinterestShareUrl } from '~/modules/page-builder/util/build-pinterest-share-url';
import type { PinItemType } from '~/modules/moodboards/type';
import {
    useTileEnquiry,
    type TileEnquiryConfig,
} from '~/modules/enquiry/hooks/use-tile-enquiry';
import { useWebsiteVisitAction } from '~/modules/tracking/hook/use-website-visit-action';
import { TILE_PRIMARY_ACTION } from './tile-primary-action';

/** Attribution for tile referrals on the `Website` event. */
const TILE_VISIT_WEBSITE_SOURCE = 'TileVisitWebsite';

/** Label shared by the desktop pill and the mobile sheet row. */
const VISIT_WEBSITE_LABEL = 'Visit site';

/** The label on the panel's second control, and on the mobile sheet's row. */
const SHARE_LABEL = 'Share';

/**
 * Shrink for narrow tiles (a 6-column Display tile is under 12rem wide). The
 * pills keep their one size wherever the tile can hold them; below these
 * container widths the full-size row no longer fits — the share control's
 * hover sweep and opened sheet overflowed the picture onto the neighbouring
 * tile. `zoom`, not a transform: the row overflows in layout, and only zoom
 * shrinks the boxes the flex row actually lays out.
 */
const TILE_ACTION_SCALE = '@max-[240px]:[zoom:0.85] @max-[200px]:[zoom:0.7]';

/** What a tile needs to offer, and attribute, a visit to the supplier's site. */
export interface TileVisitWebsiteConfig {
    /** The item's tracked website link, from the tile view model. */
    website: string;
    /** The library item the referral is attributed to. */
    libraryItemID: number;
    professionalID: number;
    professionalTitle: string;
}

/**
 * What a tile needs to offer a pin. Every field has a default read off the
 * tile's own share target and info, so a surface that already passes `share`
 * and `tileInfo` gets the action without plumbing anything: pass this only to
 * override the page, the image, or the attribution.
 */
export interface TilePinterestConfig {
    /** The page the pin links back to. Defaults to the share URL. */
    pageUrl?: string;
    /** The image to pin. Defaults to the tile's own image. */
    mediaUrl?: string;
    /** The pin's caption. Defaults to the tile's title. */
    description?: string;
    /** Attribution on the `PinterestShare` event. */
    professionalID?: number;
    libraryImageID?: number;
}

/** Minimal item info shown in the mobile action sheet header (Pinterest-style). */
export interface TileActionInfo {
    title: string;
    subtitle?: string;
    image?: string;
    /**
     * Imagery for the save gate's brand pane. Falls back to `image`; set it
     * where the two differ — a professional's `image` is its logo (drawn
     * small, in a circle), which object-covered across the pane reads as
     * broken, so the pane takes the profile photo instead.
     */
    paneImage?: string;
    /**
     * @deprecated No longer read. The media frame owns the corner and clips
     * this panel, and that corner is no longer one value across tile types.
     * Kept so the callers threading it through keep compiling.
     */
    imageShape?: 'circle' | 'square';
    /** Background fill behind the image (e.g. a professional's logo background). */
    imageBackground?: string;
}

interface TileActionPanelProps {
    /** The pin target id (item to save). */
    pinId: number;
    /** The library item id. Falls back to pinId. */
    libraryItemId?: number;
    /** The pin type — drives which item the Save flow targets. */
    pinType: PinItemType;
    /**
     * Enquiry config for tiles that have an enquiry target (product /
     * professional). Omit / null to render a More-only panel.
     */
    enquiry?: TileEnquiryConfig | null;
    /** Label for the enquiry action. Defaults to "Enquire". */
    enquiryLabel?: string;
    /**
     * Website target for the "Visit site" action. Omit / null for tiles with no
     * website (or on surfaces that don't fetch one) to leave the action out.
     */
    visitWebsite?: TileVisitWebsiteConfig | null;
    /** Share target for the Share action. Omit to hide Share. */
    share?: ShareOptions | null;
    /**
     * Overrides for the Pinterest action. The action itself needs no config —
     * it is derived from `share` and `tileInfo` — so pass this only to point
     * the pin at a different image or page than the tile's own, or to attribute
     * the event to a professional.
     */
    pinterest?: TilePinterestConfig | null;
    /** Item info for the mobile action sheet header. */
    tileInfo?: TileActionInfo;
    /** Disable all actions. */
    disabled?: boolean;
    /** Round the panel corners to match the tile. Defaults to true. */
    rounded?: boolean;
    /**
     * An extra control for the top row, set before Save. Nothing passes one
     * today; kept for a surface that needs a control the panel does not carry.
     */
    topLeading?: ReactNode;
    /**
     * Controls set immediately before Share, in the same corner. For a host
     * with actions of its own on the picture — the page builder's tagging and
     * download — so they read as one row rather than as a second cluster
     * parked elsewhere on the photograph.
     */
    shareLeading?: ReactNode;
}

/**
 * Desktop Enquire button. Pointing at it resolves the enquiry context, so
 * professionals who don't take enquiries from this visitor drop the button
 * before it can be clicked.
 *
 * White like the controls either side of it. It carried the dark fill while it
 * stood alone in a corner; in the group it only broke the row into two halves,
 * and the picture is what the panel is supposed to be showing.
 */
const TileEnquireButton = ({
    label,
    disabled,
    onEnquire,
    onPrefetch,
}: {
    label: string;
    disabled?: boolean;
    onEnquire: () => void;
    onPrefetch: () => void;
}) => (
    <button
        type="button"
        disabled={disabled}
        onPointerEnter={onPrefetch}
        onFocus={onPrefetch}
        onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onEnquire();
        }}
        className={button('lifted', 'm', 'disabled:opacity-60')}
    >
        {label}
    </button>
);

/**
 * Desktop "Visit site" pill, the left end of the tile's bottom action row
 * (Pinterest's placement). A button rather than an anchor: the panel renders
 * inside the tile's own link, and a nested `<a>` is invalid HTML the parser
 * would split.
 */
const TileVisitWebsiteButton = ({
    disabled,
    onVisit,
}: {
    disabled?: boolean;
    onVisit: () => void;
}) => (
    <button
        type="button"
        disabled={disabled}
        onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onVisit();
        }}
        className={button('lifted', 'm', 'gap-1.5 disabled:opacity-60')}
    >
        <ArrowUpRight aria-hidden="true" className="size-4" />
        {VISIT_WEBSITE_LABEL}
    </button>
);

/**
 * Save, alone in the tile's top-right corner — the anchor Pinterest uses, and
 * the one action every image on the platform offers whatever else it can.
 *
 * The one dark pill on the panel. Every control being white left nothing
 * leading, and Save is what the panel is for on an image the reader has no
 * intention of enquiring about.
 *
 * When the item is already on one of the visitor's design boards the pill
 * reads `Saved` with a filled bookmark; clicking it still opens the board
 * picker, which is where removal lives.
 */
export const TileSaveButton = ({
    ref,
    disabled,
    saved,
    onSave,
}: {
    /** The board picker positions itself against this button. */
    ref?: React.Ref<HTMLButtonElement>;
    disabled?: boolean;
    saved?: boolean;
    onSave: () => void;
}) => (
    <button
        ref={ref}
        type="button"
        disabled={disabled}
        aria-pressed={saved}
        onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSave();
        }}
        className={button('primary', 'm', 'gap-1.5 disabled:opacity-60')}
    >
        <Bookmark
            aria-hidden="true"
            className={cn('size-4', saved && 'fill-current')}
        />
        {saved ? 'Saved' : 'Save'}
    </button>
);

/** The sheet row's bookmark for an item already on one of the visitor's boards. */
const BookmarkFilled = () => <Bookmark className="fill-current" />;

/** A row in the mobile action sheet. */
const SheetActionRow = ({
    icon: Icon,
    label,
    onClick,
}: {
    // Loose on purpose: the rows carry both lucide glyphs and the brand marks
    // from `@archipro-design/icons`, which are typed differently.
    icon: ComponentType<{ className?: string }>;
    label: string;
    onClick: () => void;
}) => (
    <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3.5 px-5 py-3.5 text-left text-base font-normal text-gray-900 transition-colors active:bg-gray-100"
    >
        {/* One box for every mark, forced: the brand icons wrap their glyph in
            a span of their own and draw it at their own size whatever they are
            handed, so Pinterest came out wider than the lucide rows and pushed
            its label out of the column they share. */}
        <span className="flex size-5 shrink-0 items-center justify-center [&_svg]:size-full!">
            <Icon />
        </span>
        {label}
    </button>
);

/**
 * The hover panel every image on the platform wears — browse and detail tiles,
 * slider tiles, gallery photographs, page-builder images alike. One panel, one
 * gradient, the same five actions in the same corners, so an image behaves the
 * same way wherever the visitor meets it.
 *
 * - **Desktop** — hover-revealed over a gradient, in two anchors: `Save`
 *   top-right, and a bottom row split by what each action does — what you can
 *   do with the item ("Visit site", `Enquire`) at the left, and the two ways to
 *   pass the picture on (`Save to Pinterest`, `Share`) at the right. Every
 *   action is on the surface — nothing is behind a menu — and the layers stay
 *   visible for the whole duration of an action (board picker open) so they
 *   never vanish mid-action. An item already on one of the visitor's design
 *   boards wears its `Saved` pill (filled bookmark) at rest, without hover;
 *   the scrim and the other actions still wait for the pointer.
 * - **Mobile** — just a `•••` button (the rest is tucked inside), opening a
 *   bottom sheet with the item's info (image, title, supplier) and the same
 *   actions as rows, so it's clear which tile the sheet is for.
 *
 * Actions self-hide when the tile can't offer them: no website, no share
 * target, or a professional who takes no enquiries from this visitor.
 *
 * Share opens the app-wide sharing modal (a centred drawer), Save the design
 * board picker, and Pinterest their pin composer in a new tab.
 */
export const TileActionPanel = ({
    pinId,
    libraryItemId,
    pinType,
    enquiry,
    enquiryLabel = 'Enquire',
    visitWebsite,
    share,
    pinterest,
    tileInfo,
    disabled = false,
    rounded = true,
    topLeading,
    shareLeading,
}: TileActionPanelProps) => {
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const [open, setOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const saveButtonRef = useRef<HTMLButtonElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const setShareTarget = useSetAtom(shareTargetAtom);
    const openShareModal = useSetAtom(uiStateAtomSocialSharingModalIsOpened);
    const { isInDesignBoard } = useHandleFollow();
    const isSaved = pinId ? isInDesignBoard(pinId, pinType) : false;
    const {
        followSelector,
        openFollowSelector,
        isOpen: followSelectorOpen,
    } = useFollowSelectorProxy({
        pinId,
        pinType,
        libraryItemId,
        title: tileInfo?.title,
        image: tileInfo?.paneImage ?? tileInfo?.image,
    });
    // Owned by the panel, not by the button or sheet row: the mobile row closes
    // the sheet as it opens the modal, and a hook unmounting with the sheet
    // would take the pending enrichment (and the audience withdrawal) with it.
    const {
        onEnquire,
        prefetch: prefetchEnquiry,
        showEnquire,
    } = useTileEnquiry(enquiry ?? null);

    // Unconditional so hook order is stable across tiles with and without a
    // website; the action resolves to a null URL when there is nothing to open,
    // and the button is left out.
    const { websiteURL, visitWebsite: onVisitWebsite } = useWebsiteVisitAction({
        website: visitWebsite?.website,
        libraryItemID: visitWebsite?.libraryItemID ?? 0,
        professionalID: visitWebsite?.professionalID ?? 0,
        professionalTitle: visitWebsite?.professionalTitle ?? '',
        source: TILE_VISIT_WEBSITE_SOURCE,
    });
    /**
     * The one action the tile leads with — see {@link TILE_PRIMARY_ACTION}.
     * Whichever of the two is not the platform's choice is off the panel
     * entirely, so a tile that cannot offer the chosen one offers neither.
     */
    const showVisitWebsite = TILE_PRIMARY_ACTION === 'website' && !!websiteURL;
    const showEnquireAction = TILE_PRIMARY_ACTION === 'enquiry' && showEnquire;

    // Resolve the enquiry context as the sheet opens — it settles long before
    // the user has read the sheet and tapped, so a gated professional's row is
    // already gone rather than disappearing under the thumb.
    useEffect(() => {
        if (!open || !isMobile) return;
        prefetchEnquiry();
    }, [open, isMobile, prefetchEnquiry]);

    const handleSave = useCallback(() => {
        setOpen(false);
        // Anchor the board picker to the Save button, not to the strip holding
        // it: that strip is seven tenths of the tile tall and spans its width,
        // so a picker positioned against it opened from a corner of the
        // photograph rather than from the pill that was clicked.
        openFollowSelector(
            saveButtonRef.current ?? panelRef.current ?? undefined,
        );
    }, [openFollowSelector]);

    /**
     * What the sheet shares. Tiles pass a relative link, so it is resolved
     * against the origin; the picture travels with it because one of the
     * targets shares an image rather than a page.
     */
    const shareTarget = useMemo<ShareOptions | null>(() => {
        if (!share) return null;
        const url =
            share.url.startsWith('http') || typeof window === 'undefined'
                ? share.url
                : new URL(share.url, window.location.origin).href;
        return { ...share, url, image: pinterest?.mediaUrl ?? tileInfo?.image };
    }, [share, pinterest?.mediaUrl, tileInfo?.image]);

    /**
     * The mobile row's Share: the phone's own sheet, which is where a reader
     * on a phone keeps the apps they actually send things to. Our sheet is the
     * fallback for a browser that has no share of its own.
     */
    const handleShare = useCallback(() => {
        setOpen(false);
        if (!shareTarget) return;
        if (isNativeShareAvailable()) {
            void shareNative(shareTarget);
            return;
        }
        setShareTarget(shareTarget);
        openShareModal(true);
    }, [shareTarget, setShareTarget, openShareModal]);

    /**
     * Pinterest gets its own row on a phone. The OS sheet is whatever the
     * phone offers and the pin is rarely in it, so the one target this
     * audience reaches for would otherwise be the one they could not take.
     */
    const pinterestMedia = pinterest?.mediaUrl ?? tileInfo?.image;
    const showPinterestRow = !!shareTarget && !!pinterestMedia;

    const handlePinterest = useCallback(() => {
        setOpen(false);
        if (!shareTarget || !pinterestMedia) return;
        window.open(
            buildPinterestShareUrl({
                pageUrl: shareTarget.url,
                mediaUrl: pinterestMedia,
                description: pinterest?.description ?? tileInfo?.title,
            }),
            '_blank',
            'noopener,noreferrer',
        );
    }, [shareTarget, pinterestMedia, pinterest?.description, tileInfo?.title]);

    const stop = useCallback((e: React.MouseEvent) => {
        // The whole tile is a link; keep clicks on the panel from navigating.
        e.preventDefault();
        e.stopPropagation();
    }, []);

    // While the sheet is open (and briefly after it closes), swallow clicks on
    // *this tile's* link. Tapping the backdrop to dismiss otherwise falls
    // through to the tile `<a>` underneath and navigates to the detail page. The
    // swallow must be armed for the whole open duration — vaul can close on the
    // click itself, after it has already propagated to the link.
    //
    // Scoped to the tile that owns this panel: the trigger is rendered as a
    // sibling of the tile link, so their common parent bounds the one anchor a
    // dismiss tap can hit. Swallowing every anchor would silently eat the next
    // tap anywhere on the page — another tile, the nav — for 350ms.
    useEffect(() => {
        if (!open || !isMobile) return;
        const tileRoot = triggerRef.current?.parentElement;
        if (!tileRoot) return;
        const swallow = (e: MouseEvent) => {
            if (!(e.target instanceof Element)) return;
            const anchor = e.target.closest('a');
            if (!anchor || !tileRoot.contains(anchor)) return;
            e.preventDefault();
            e.stopPropagation();
        };
        document.addEventListener('click', swallow, true);
        return () => {
            window.setTimeout(() => {
                document.removeEventListener('click', swallow, true);
            }, 350);
        };
    }, [open, isMobile]);

    // ── Mobile: `•••` → bottom sheet with item info + all actions ──────────
    if (isMobile) {
        return (
            <>
                <button
                    ref={triggerRef}
                    type="button"
                    disabled={disabled}
                    aria-label="More actions"
                    onClick={(e) => {
                        stop(e);
                        setOpen(true);
                    }}
                    className={button(
                        'lifted',
                        'm',
                        // `pointer-events-auto` because some hosts lay this
                        // panel in a strip that takes none, so a click still
                        // reaches the picture. The trigger has to opt back in
                        // or it is visible and dead.
                        'pointer-events-auto absolute top-3 right-3 z-10 disabled:opacity-60',
                        { icon: true },
                    )}
                >
                    <MoreHorizontal className="size-4" />
                </button>

                <Drawer open={open} onOpenChange={setOpen} direction="bottom">
                    <DrawerContent noHandle>
                        <DrawerTitle className="sr-only">
                            {tileInfo?.title ?? 'Tile actions'}
                        </DrawerTitle>
                        <DrawerDescription className="hidden" aria-hidden />

                        {tileInfo ? (
                            <div className="flex items-center gap-3 pt-5 pr-14 pb-4 pl-5">
                                {tileInfo.image ? (
                                    <img
                                        src={tileInfo.image}
                                        alt=""
                                        style={
                                            tileInfo.imageBackground
                                                ? {
                                                      backgroundColor:
                                                          tileInfo.imageBackground,
                                                  }
                                                : undefined
                                        }
                                        className={cn(
                                            'size-14 shrink-0',
                                            !tileInfo.imageBackground &&
                                                'bg-[#f3f3f5]',
                                            tileInfo.imageShape === 'circle'
                                                ? 'rounded-full object-contain'
                                                : 'rounded-xl object-cover',
                                        )}
                                    />
                                ) : null}
                                <div className="min-w-0">
                                    <p className="truncate font-medium text-gray-900">
                                        {tileInfo.title}
                                    </p>
                                    {tileInfo.subtitle ? (
                                        <p className="truncate text-sm text-gray-500">
                                            {tileInfo.subtitle}
                                        </p>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}

                        <div className="flex flex-col border-t border-gray-100 pb-[max(env(safe-area-inset-bottom),0.5rem)]">
                            {showEnquireAction ? (
                                <SheetActionRow
                                    icon={MessageSquareText}
                                    label={enquiryLabel}
                                    onClick={() => {
                                        setOpen(false);
                                        onEnquire();
                                    }}
                                />
                            ) : null}
                            {showVisitWebsite ? (
                                <SheetActionRow
                                    icon={ArrowUpRight}
                                    label={VISIT_WEBSITE_LABEL}
                                    onClick={() => {
                                        setOpen(false);
                                        onVisitWebsite();
                                    }}
                                />
                            ) : null}
                            <SheetActionRow
                                icon={isSaved ? BookmarkFilled : Bookmark}
                                label={isSaved ? 'Saved' : 'Save'}
                                onClick={handleSave}
                            />
                            {showPinterestRow ? (
                                <SheetActionRow
                                    icon={PinterestFill}
                                    label="Save to Pinterest"
                                    onClick={handlePinterest}
                                />
                            ) : null}
                            {share ? (
                                <SheetActionRow
                                    icon={Share}
                                    label={SHARE_LABEL}
                                    onClick={handleShare}
                                />
                            ) : null}
                        </div>
                    </DrawerContent>
                </Drawer>
                {followSelector}
            </>
        );
    }

    // ── Desktop: [Save] top-right, [Visit site] … [More] along the bottom.
    // Held open while the board picker is up, so the panel cannot vanish from
    // under an action the visitor is still in the middle of.
    const revealed =
        followSelectorOpen || shareOpen
            ? 'opacity-100'
            : 'opacity-100 md:opacity-0 md:group-hover:opacity-100';
    return (
        <>
            {/* Also the board picker's anchor, which is why it stays mounted
                whatever the tile can offer. */}
            <div
                ref={panelRef}
                onClick={stop}
                className={cn(
                    'absolute inset-x-0 top-0 z-10 flex items-start justify-end gap-2 max-md:p-3 md:p-4',
                    // The strip is as wide as the tile, so its container size
                    // is what the pills scale against below.
                    '@container',
                    // Seven tenths of the picture, so the scrim has room to
                    // fade out rather than banding just below the pills. The
                    // strip takes no pointer events, so its height costs the
                    // tile nothing — only the pills take clicks back.
                    'h-[70%]',
                    // The strip spans the tile, so it must not be hit-testable:
                    // a click on the empty half of it is a click on the tile,
                    // and belongs to the link underneath. Only the pills take
                    // pointer events back.
                    'pointer-events-none',
                    // Always mounted at full opacity: the hover reveal lives on
                    // the scrim and the pill spans inside, because a saved pill
                    // stays visible without hover and a child can never be more
                    // opaque than a faded parent.
                )}
            >
                {/* The hover scrim, faded as one with the unsaved pills. */}
                <div
                    aria-hidden="true"
                    className={cn(
                        'absolute inset-0 transition-opacity',
                        // Below the pills: an absolute child paints above the
                        // strip's in-flow spans whatever the DOM order, so on
                        // hover the gradient washed over the Saved pill. The
                        // strip's own z-10 bounds the stacking context, so the
                        // negative index cannot drop the scrim under the
                        // photograph.
                        '-z-[1]',
                        'bg-linear-to-b from-black/25 to-transparent',
                        revealed,
                        // The scrim needs a corner of its own: on the browse
                        // grids it is a sibling of the media and nothing clips
                        // it, so a square scrim squares the tile on hover. On
                        // the product-detail grid it sits inside the media and
                        // IS clipped, so it has to match that frame exactly or
                        // it notches the corner. The media publishes its corner
                        // as a custom property there; elsewhere the fallback
                        // applies.
                        rounded &&
                            'rounded-t-[var(--tile-corner,var(--radius-tile))]',
                    )}
                />
                {topLeading ? (
                    <span
                        className={cn(
                            'pointer-events-auto transition-opacity',
                            revealed,
                            TILE_ACTION_SCALE,
                        )}
                    >
                        {topLeading}
                    </span>
                ) : null}
                <span
                    className={cn(
                        'pointer-events-auto transition-opacity',
                        // The saved state is the one thing the tile shows at
                        // rest: an already-saved pill holds its place whether
                        // or not the pointer is anywhere near the tile.
                        isSaved ? 'opacity-100' : revealed,
                        TILE_ACTION_SCALE,
                    )}
                >
                    <TileSaveButton
                        ref={saveButtonRef}
                        disabled={disabled}
                        saved={isSaved}
                        onSave={handleSave}
                    />
                </span>
            </div>

            <div
                onClick={stop}
                className={cn(
                    'absolute inset-x-0 bottom-0 z-10 flex items-end justify-between gap-2 max-md:p-3 md:p-4',
                    // The tile is what decides whether a label has room to
                    // open, not the viewport: the same screen carries a
                    // 237px grid tile and a 446px carousel tile.
                    '@container',
                    // The mirror of the top strip, for the same reason: the
                    // gradient needs room to fade or it bands just above the
                    // pills. The two overlap across the middle of the tile,
                    // where both have already fallen to nothing.
                    'h-[70%]',
                    'transition-opacity',
                    'bg-linear-to-t from-black/25 to-transparent',
                    rounded &&
                        'rounded-b-[var(--tile-corner,var(--radius-tile))]',
                    // As above — and the gap between the two ends sits over
                    // TileMedia's hover carousel controls, which have to keep
                    // receiving their own clicks.
                    'pointer-events-none',
                    revealed,
                )}
            >
                {/* The one thing to do with the item, at the left — Visit
                    site or Enquire, never one standing in for the other. See
                    TILE_PRIMARY_ACTION for why. Always mounted, even when the
                    item can offer neither, so Share stays hard right. */}
                <span
                    className={cn(
                        'pointer-events-auto flex items-end gap-2',
                        TILE_ACTION_SCALE,
                    )}
                >
                    {showVisitWebsite ? (
                        <TileVisitWebsiteButton
                            disabled={disabled}
                            onVisit={onVisitWebsite}
                        />
                    ) : showEnquireAction ? (
                        <TileEnquireButton
                            label={enquiryLabel}
                            disabled={disabled}
                            onEnquire={onEnquire}
                            onPrefetch={prefetchEnquiry}
                        />
                    ) : null}
                </span>

                {/* Everything else, behind one control: the sheet it opens
                    carries Copy link, Pinterest and the social targets. */}
                {/* The host's own controls and Share, one row in one
                    corner. */}
                {shareLeading || shareTarget ? (
                    <span
                        className={cn(
                            'pointer-events-auto flex items-end gap-2',
                            TILE_ACTION_SCALE,
                        )}
                    >
                        {shareLeading}
                        {shareTarget ? (
                            <TileShareMenu
                                share={shareTarget}
                                open={shareOpen}
                                onOpenChange={setShareOpen}
                                disabled={disabled}
                            />
                        ) : null}
                    </span>
                ) : null}
            </div>
            {followSelector}
        </>
    );
};
